import to from 'await-to-js';
import type { LoginRequest, LoginResponse } from '$types/matrix-sdk';
import { createClient, MatrixError } from '$types/matrix-sdk';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSetAtom } from 'jotai';
import * as Sentry from '@sentry/react';
import type { ClientConfig } from '$hooks/useClientConfig';
import { clientAllowedServer } from '$hooks/useClientConfig';
import {
  deleteAfterLoginRedirectPath,
  getAfterLoginRedirectPath,
} from '$pages/afterLoginRedirectPath';
import { clearPendingSlidingSyncLogin } from './slidingSyncLogin';
import { getHomePath } from '$pages/pathUtils';
import { activeSessionIdAtom, sessionsAtom, type Session } from '$state/sessions';
import { createLogger } from '$utils/debug';
import { createDebugLogger } from '$utils/debugLogger';
import { fetch } from '$utils/fetch';
import { ErrorCode } from '../../../cs-errorcode';
import { autoDiscovery, specVersions } from '../../../cs-api';

const log = createLogger('loginUtil');
const debugLog = createDebugLogger('loginUtil');

enum GetBaseUrlError {
  NotAllow = 'NotAllow',
  NotFound = 'NotFound',
}
export const factoryGetBaseUrl =
  (clientConfig: ClientConfig, server: string) => async (): Promise<string> => {
    if (!clientAllowedServer(clientConfig, server)) {
      throw new Error(GetBaseUrlError.NotAllow);
    }

    const [, discovery] = await to(autoDiscovery(fetch, server));

    let mxIdBaseUrl: string | undefined;
    const [, discoveryInfo] = discovery ?? [];

    if (discoveryInfo) {
      mxIdBaseUrl = discoveryInfo['m.homeserver'].base_url;
    }

    if (!mxIdBaseUrl) {
      throw new Error(GetBaseUrlError.NotFound);
    }
    const [, versions] = await to(specVersions(fetch, mxIdBaseUrl));
    if (!versions) {
      throw new Error(GetBaseUrlError.NotFound);
    }
    return mxIdBaseUrl;
  };

export enum LoginError {
  ServerNotAllowed = 'ServerNotAllowed',
  InvalidServer = 'InvalidServer',
  Forbidden = 'Forbidden',
  UserDeactivated = 'UserDeactivated',
  InvalidRequest = 'InvalidRequest',
  RateLimited = 'RateLimited',
  Unknown = 'Unknown',
}

export type CustomLoginResponse = {
  baseUrl: string;
  response: LoginResponse;
};
export const login = async (
  serverBaseUrl: string | (() => Promise<string>),
  data: LoginRequest
): Promise<CustomLoginResponse> => {
  const [urlError, url] =
    typeof serverBaseUrl === 'function' ? await to(serverBaseUrl()) : [undefined, serverBaseUrl];
  if (urlError) {
    throw new MatrixError({
      errcode:
        urlError.message === (GetBaseUrlError.NotAllow as string)
          ? LoginError.ServerNotAllowed
          : LoginError.InvalidServer,
    });
  }

  const mx = createClient({ baseUrl: url, fetchFn: fetch });
  debugLog.info('general', 'Attempting login', { baseUrl: url, loginType: data.type });

  return Sentry.startSpan(
    { name: 'auth.login', op: 'auth', attributes: { 'auth.method': data.type } },
    async (span) => {
      const [err, res] = await to<LoginResponse, MatrixError>(mx.loginRequest(data));

      if (err) {
        span.setAttribute('auth.error', err.errcode ?? 'unknown');
        Sentry.metrics.count('sable.auth.login_failed', 1, {
          attributes: { errcode: err.errcode ?? 'unknown' },
        });
        if (err.httpStatus === 400) {
          debugLog.error('general', 'Login failed - invalid request', {
            httpStatus: 400,
          });
          throw new MatrixError({
            errcode: LoginError.InvalidRequest,
          });
        }
        if (err.httpStatus === 429) {
          debugLog.error('general', 'Login failed - rate limited', { httpStatus: 429 });
          throw new MatrixError({
            errcode: LoginError.RateLimited,
          });
        }
        if (err.errcode === (ErrorCode.M_USER_DEACTIVATED as string)) {
          debugLog.error('general', 'Login failed - user deactivated', {
            errcode: err.errcode,
          });
          throw new MatrixError({
            errcode: LoginError.UserDeactivated,
          });
        }

        if (err.httpStatus === 403) {
          debugLog.error('general', 'Login failed - forbidden', { httpStatus: 403 });
          throw new MatrixError({
            errcode: LoginError.Forbidden,
          });
        }

        debugLog.error('general', 'Login failed - unknown error', {
          error: err.message,
          httpStatus: err.httpStatus,
        });
        throw new MatrixError({
          error: err.message,
          errcode: LoginError.Unknown,
        });
      }

      span.setAttribute('auth.success', true);
      debugLog.info('general', 'Login successful', {
        userId: res.user_id,
        deviceId: res.device_id,
      });
      return {
        baseUrl: url,
        response: res,
      };
    }
  );
};

/**
 * Commits a session to the store, makes it active, and navigates into the app. Shared by the
 * password/token login path and the OIDC callback.
 */
export const useCommitLoginSession = () => {
  const navigate = useNavigate();
  const setSessions = useSetAtom(sessionsAtom);
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);

  return useCallback(
    (session: Session, slidingSyncOptIn?: boolean) => {
      const committedSession =
        slidingSyncOptIn === undefined ? session : { ...session, slidingSyncOptIn };
      setSessions({ type: 'PUT', session: committedSession });
      setActiveSessionId(committedSession.userId);
      clearPendingSlidingSyncLogin();
      const afterLoginRedirectUrl = getAfterLoginRedirectPath();
      deleteAfterLoginRedirectPath();
      const destination = afterLoginRedirectUrl ?? getHomePath();
      log.log('commitLoginSession: navigating to', destination);
      navigate(destination, { replace: true });
    },
    [navigate, setSessions, setActiveSessionId]
  );
};

export const useLoginComplete = (data?: CustomLoginResponse, slidingSyncOptIn?: boolean) => {
  const commitSession = useCommitLoginSession();

  useEffect(() => {
    if (data) {
      const { response: loginRes, baseUrl: loginBaseUrl } = data;
      log.log('useLoginComplete: login success', {
        userId: loginRes.user_id,
        baseUrl: loginBaseUrl,
      });
      const newSession: Session = {
        baseUrl: loginBaseUrl,
        userId: loginRes.user_id,
        deviceId: loginRes.device_id,
        accessToken: loginRes.access_token,
      };
      commitSession(newSession, slidingSyncOptIn);
    }
  }, [data, slidingSyncOptIn, commitSession]);
};
